import type { Order, OrderFilters, CreateOrderData, UpdateOrderData } from "@/types/order";

const now = new Date();
const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// Mock data per ordini
const mockOrders: Order[] = [
    {
        id: "1",
        receiverId: "2", // Global Partners Corp
        shipperId: "1", // TechCorp Solutions LLC
        status: "Pending",
        targetWeek: getWeekNumber(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14)),
        targetYear: now.getFullYear(),
        packingList: {
            requested: "Packing list dettagliata con quantità, peso e dimensioni per ogni articolo. Richiesta certificazione di origine per prodotti elettronici."
        },
        notes: "Ordine urgente per progetto Q1 2024",
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
    },
    {
        id: "2",
        receiverId: "2", // Global Partners Corp
        shipperId: "3", // Innovation Hub Partnership
        status: "Accepted",
        targetWeek: getWeekNumber(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 21)),
        targetYear: now.getFullYear(),
        packingList: {
            requested: "Packing list con codici prodotto, quantità e valori unitari",
            provided: "Packing list conforme alle specifiche richieste. Tutti i codici prodotto sono inclusi con quantità e valori.",
            uploadedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)
        },
        contractualInfo: {
            contractNumber: "CONTRACT-2024-001",
            contractDate: new Date("2024-01-15"),
            terms: "FOB, pagamento 30 giorni",
            notes: "Contratto standard per partnership"
        },
        orderItems: [
            { id: "1", description: "Componenti elettronici - Modello A", quantity: 100, unit: "pz" },
            { id: "2", description: "Componenti elettronici - Modello B", quantity: 50, unit: "pz" }
        ],
        notes: "Ordine confermato per progetto innovazione",
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        respondedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
    },
    {
        id: "3",
        receiverId: "2", // Global Partners Corp
        shipperId: "1", // TechCorp Solutions LLC
        status: "Modified",
        targetWeek: getWeekNumber(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 28)),
        targetYear: now.getFullYear(),
        packingList: {
            requested: "Packing list con dettagli completi per ogni articolo",
            provided: "Packing list parziale - alcuni dettagli da completare"
        },
        proposedChanges: "Richiesta di estendere la data di consegna di una settimana per permettere il completamento della documentazione richiesta.",
        changesAccepted: false,
        notes: "Ordine con modifiche proposte",
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        respondedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
    },
    {
        id: "4",
        receiverId: "4", // Green Future NGO
        shipperId: "1", // TechCorp Solutions LLC
        status: "Rejected",
        targetWeek: getWeekNumber(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7)),
        targetYear: now.getFullYear(),
        packingList: {
            requested: "Packing list con certificazioni ambientali"
        },
        rejectionReason: "Non possiamo soddisfare i requisiti di certificazione ambientale richiesti nel tempo indicato.",
        notes: "Ordine rifiutato per incompatibilità con certificazioni",
        createdAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        respondedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
    },
];

const LOCAL_STORAGE_KEY = "mockOrders";

// Funzione helper per deserializzare le date dagli ordini salvati
function deserializeOrder(order: any): Order {
    return {
        ...order,
        createdAt: new Date(order.createdAt),
        updatedAt: new Date(order.updatedAt),
        respondedAt: order.respondedAt ? new Date(order.respondedAt) : undefined,
        confirmedAt: order.confirmedAt ? new Date(order.confirmedAt) : undefined,
        packingList: {
            ...order.packingList,
            uploadedAt: order.packingList?.uploadedAt ? new Date(order.packingList.uploadedAt) : undefined,
        },
        contractualInfo: order.contractualInfo ? {
            ...order.contractualInfo,
            contractDate: order.contractualInfo.contractDate ? new Date(order.contractualInfo.contractDate) : undefined,
        } : undefined,
    };
}

// Carica ordini da localStorage o usa i mock iniziali
function loadOrders(): Order[] {
    if (typeof window === 'undefined') {
        return [...mockOrders];
    }
    
    try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Combina ordini salvati con mock iniziali (evita duplicati)
            const storedOrders = parsed.map(deserializeOrder);
            const mockIds = new Set(mockOrders.map(o => o.id));
            const newStoredOrders = storedOrders.filter((o: Order) => !mockIds.has(o.id));
            return [...mockOrders, ...newStoredOrders];
        }
    } catch (error) {
        console.error('Error loading orders from localStorage:', error);
    }
    
    return [...mockOrders];
}

// Salva ordini in localStorage
function saveOrders(): void {
    if (typeof window === 'undefined') return;
    
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(orders));
    } catch (error) {
        console.error('Error saving orders to localStorage:', error);
    }
}

// In-memory storage (inizializzato da localStorage o mock)
let orders = loadOrders();

// Helper functions per CRUD operations
export const orderService = {
    // Get all orders
    getAll: (): Order[] => {
        return [...orders];
    },

    // Get order by ID
    getById: (id: string): Order | undefined => {
        return orders.find((order) => order.id === id);
    },

    // Get orders by receiver ID
    getByReceiverId: (receiverId: string): Order[] => {
        return orders.filter((order) => order.receiverId === receiverId);
    },

    // Get orders by shipper ID
    getByShipperId: (shipperId: string): Order[] => {
        return orders.filter((order) => order.shipperId === shipperId);
    },

    // Create new order
    create: (data: CreateOrderData): Order => {
        // Genera un ID univoco basato su timestamp per evitare conflitti
        const newId = `order-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newOrder: Order = {
            id: newId,
            ...data,
            status: "Pending",
            packingList: {
                requested: data.packingList.requested
            },
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        orders.push(newOrder);
        saveOrders(); // Salva in localStorage
        return newOrder;
    },

    // Update order
    update: (id: string, data: UpdateOrderData): Order | null => {
        const index = orders.findIndex((order) => order.id === id);
        if (index === -1) return null;

        const updateData: Partial<Order> = { ...data };
        
        // Gestione packing list
        if (data.packingList) {
            updateData.packingList = {
                ...orders[index].packingList,
                ...data.packingList
            };
        }

        // Gestione timestamps in base allo status
        if (data.status) {
            if (data.status === "Accepted" || data.status === "Rejected" || data.status === "Modified") {
                updateData.respondedAt = new Date();
            }
            if (data.status === "Confirmed") {
                updateData.confirmedAt = new Date();
            }
        }

        orders[index] = {
            ...orders[index],
            ...updateData,
            updatedAt: new Date(),
        };
        saveOrders(); // Salva in localStorage
        return orders[index];
    },

    // Delete order
    delete: (id: string): boolean => {
        const index = orders.findIndex((order) => order.id === id);
        if (index === -1) return false;

        orders.splice(index, 1);
        saveOrders(); // Salva in localStorage
        return true;
    },

    // Search and filter orders
    search: (filters: OrderFilters): Order[] => {
        let filtered = [...orders];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter((order) => 
                order.notes?.toLowerCase().includes(searchTerm) ||
                order.packingList.requested?.toLowerCase().includes(searchTerm) ||
                order.contractualInfo?.contractNumber?.toLowerCase().includes(searchTerm)
            );
        }

        if (filters.status) {
            filtered = filtered.filter((order) => order.status === filters.status);
        }

        if (filters.receiverId) {
            filtered = filtered.filter((order) => order.receiverId === filters.receiverId);
        }

        if (filters.shipperId) {
            filtered = filtered.filter((order) => order.shipperId === filters.shipperId);
        }

        if (filters.targetYear) {
            filtered = filtered.filter((order) => order.targetYear === filters.targetYear);
        }

        if (filters.targetWeek) {
            filtered = filtered.filter((order) => order.targetWeek === filters.targetWeek);
        }

        return filtered;
    },

    // Get order statistics
    getStats: (receiverId?: string, shipperId?: string): { 
        total: number; 
        pending: number; 
        accepted: number; 
        rejected: number; 
        modified: number;
        confirmed: number;
        cancelled: number;
    } => {
        let filteredOrders = [...orders];
        
        if (receiverId) {
            filteredOrders = filteredOrders.filter((o) => o.receiverId === receiverId);
        }
        
        if (shipperId) {
            filteredOrders = filteredOrders.filter((o) => o.shipperId === shipperId);
        }

        return {
            total: filteredOrders.length,
            pending: filteredOrders.filter((o) => o.status === "Pending").length,
            accepted: filteredOrders.filter((o) => o.status === "Accepted").length,
            rejected: filteredOrders.filter((o) => o.status === "Rejected").length,
            modified: filteredOrders.filter((o) => o.status === "Modified").length,
            confirmed: filteredOrders.filter((o) => o.status === "Confirmed").length,
            cancelled: filteredOrders.filter((o) => o.status === "Cancelled").length,
        };
    },
};

